'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Maximize2, X } from 'lucide-react';
import { getAvailableUpscalePricing } from '@/lib/api/upscale';
import { useFlags } from '@/lib/hooks/useFlags';
import GemCost from '../create/generatorViewPort/gemCost';
import Leaflet from '../shared/leaflet';
import useWindowSize from '@/lib/hooks/use-window-size';

interface UpscalePricing {
  factor: number;
  cost: number;
}

interface UpscaleButtonProps {
  onUpscale: (factor: number) => void;
  upscaleTranslations: any;
  disabled?: boolean;
  color?: string;
  generationId: string;
  upscalePricing?: UpscalePricing[];
  upscaleButtonContent?: React.ReactNode;
  upscaleButtonStyle?: string;
}

const UpscaleButton: React.FC<UpscaleButtonProps> = ({
  onUpscale,
  upscaleTranslations,
  disabled = false,
  color = '#0772DD',
  generationId,
  upscalePricing: propUpscalePricing,
  upscaleButtonContent,
  upscaleButtonStyle = 'inline-flex items-center justify-center gap-x-1 border border-transparent border-l-grey-outline px-4 text-xs font-semibold text-[#23E000] transition-all hover:brightness-110 focus:z-10 disabled:opacity-50'
}) => {
  const { isMobile } = useWindowSize();
  const { data: flagsData } = useFlags();
  const upscaleEnabledFlag = flagsData?.['upscale-enabled'];
  const [isOpen, setIsOpen] = useState(false);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [localUpscalePricing, setLocalUpscalePricing] = useState<UpscalePricing[]>(
    propUpscalePricing || []
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasFlex1 = upscaleButtonStyle.includes('flex-1');
  const wrapperClasses = hasFlex1 ? 'relative flex flex-1 w-full' : 'relative inline-flex';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && isOpen) return;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !isUpscaling
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUpscaling, isOpen]);

  const toggleMenu = () => !disabled && !isUpscaling && setIsOpen(!isOpen);

  const handleUpscale = async (factor: number) => {
    if (isUpscaling) return; // Prevent double-clicking

    try {
      setIsUpscaling(true);
      await onUpscale(factor);
      // Close dropdown on success
      setIsOpen(false);
    } catch (error) {
      console.error('Error during upscale:', error);
      // Keep dropdown open on error so user can retry
    } finally {
      setIsUpscaling(false);
    }
  };

  const handleClose = () => {
    if (!isUpscaling) {
      setIsOpen(false);
    }
  };

  // Fetch pricing if not provided via props
  useEffect(() => {
    if (!propUpscalePricing) {
      const fetchPricing = async () => {
        const response = await getAvailableUpscalePricing(generationId);
        if (response.upscalePricing?.length > 0) {
          setLocalUpscalePricing(response.upscalePricing);
        }
      };
      fetchPricing();
    }
  }, [generationId, propUpscalePricing]);

  const defaultContent = (
    <>
      {' '}
      <Maximize2 size={14} />
      <span>{isUpscaling ? 'Upscaling...' : upscaleTranslations.upscale}</span>
    </>
  );

  const UpscaleContent = (
    <div className="space-y-3">
      {localUpscalePricing.map((pricing) => (
        <button
          key={pricing.factor}
          onClick={() => handleUpscale(pricing.factor)}
          disabled={isUpscaling}
          className={`flex w-full justify-between rounded-md bg-[#37393C] p-3 text-sm font-medium text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:ring-0`}
        >
          <span>
            {upscaleTranslations.upscale} {pricing.factor}x
          </span>

          <GemCost amount={pricing.cost} />
        </button>
      ))}
    </div>
  );

  // Don't render if upscale is disabled
  if (!upscaleEnabledFlag?.enabled) {
    return null;
  }

  return (
    <>
      {localUpscalePricing && localUpscalePricing.length > 0 && (
        <>
          <div className={wrapperClasses} ref={wrapperRef}>
            <button
              type="button"
              className={upscaleButtonStyle}
              onClick={toggleMenu}
              disabled={disabled || isUpscaling}
            >
              {upscaleButtonContent || defaultContent}
            </button>

            {isOpen && (
              <div className="hidden sm:block">
                <div className="top-29 absolute bottom-full right-0 z-[1] mb-2 w-64 origin-bottom-right rounded-lg border border-grey-outline bg-crp-grey p-4 shadow-2xl sm:bottom-auto sm:left-0 sm:top-9 sm:mb-0 sm:mt-2 sm:w-80 sm:origin-top-left">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white">
                      {upscaleTranslations.upscaleImage}
                    </h3>
                    <button
                      onClick={handleClose}
                      disabled={isUpscaling}
                      className="rounded-full text-gray-500 hover:brightness-150 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <p className="pb-4 text-xs text-white">
                    {upscaleTranslations.upscaleImageDescription}
                  </p>
                  {UpscaleContent}
                </div>
              </div>
            )}
          </div>
          {isOpen && (
            <div className="sm:hidden">
              <Leaflet setShow={setIsOpen}>
                <div className="w-full px-5 pb-4 pt-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white">
                      {upscaleTranslations.upscaleImage}
                    </h3>
                  </div>
                  <p className="pb-4 text-xs text-white">
                    {upscaleTranslations.upscaleImageDescription}
                  </p>
                  {UpscaleContent}
                </div>
              </Leaflet>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default UpscaleButton;
